import React, { Component, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { Portal, Snackbar, SnackbarOrigin, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { INotifyComponentProps } from '../providers/NotifyContext';

const styles = (theme: Theme) =>
  createStyles({
    contentRoot: {
      top: theme.mixins.toolbar.minHeight,
    },
    infoSolid: {
      backgroundColor: theme.custom.palette.green2,
    },
    info: {
      backgroundColor: theme.palette.common.white,
      borderLeft: `8px solid ${theme.custom.palette.green2}`,
    },
    error: {
      backgroundColor: theme.palette.common.white,
      borderLeft: `8px solid ${theme.custom.palette.red}`,
    },
    message: {
      display: 'flex',
      alignItems: 'center',
    },
    right: {},
  });

interface IProps extends INotifyComponentProps, WithStyles<typeof styles> {}

interface IState {
  open: boolean;
}

class Notify extends Component<IProps> {
  state: IState = {
    open: false,
  };

  componentDidUpdate(prevProps: IProps) {
    const { show } = this.props;

    if (prevProps.show !== show) {
      this.setState({ open: true });
    }
  }

  getOrigin(): SnackbarOrigin {
    const { placement } = this.props;

    if (placement === 'top') {
      return { vertical: 'top', horizontal: 'center' };
    }

    if (placement === 'right') {
      return { vertical: 'top', horizontal: 'right' };
    }

    if (placement === 'bottomLeft') {
      return { vertical: 'bottom', horizontal: 'left' };
    }

    return null;
  }

  handleClose = () => {
    const { onClose } = this.props;
    const { open } = this.state;

    this.setState({ open: !open }, onClose);
  };

  renderMessage(children: ReactNode | string) {
    return (
      <Typography align="center" variant="subtitle2">
        {children}
      </Typography>
    );
  }

  renderNotify() {
    const { i18n, message, component } = this.props;

    if (i18n) {
      return this.renderMessage(<Trans {...i18n} />);
    }

    if (message) {
      return this.renderMessage(message);
    }

    if (component) {
      return component;
    }

    return null;
  }

  render() {
    const { classes, duration, placement, type } = this.props;
    const { open } = this.state;

    const rootClasses = classNames(classes[type], classes[placement], classes.contentRoot);
    const contentClasses = { root: rootClasses };
    const anchorOrigin = this.getOrigin();

    return (
      <Portal>
        <Snackbar
          open={open}
          autoHideDuration={duration}
          ContentProps={{ classes: contentClasses }}
          anchorOrigin={anchorOrigin}
          message={this.renderNotify()}
          onClose={this.handleClose}
        />
      </Portal>
    );
  }
}

export default withStyles(styles)(Notify);
